import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { LocalizationModule } from '@abp/ng.core';
import { safeJsonParse } from 'src/core/helpers/json-parse.helper';
import { NotificationDto } from 'src/core/models/notification/notification.dto';
import {
  ContactImportNotificationPayload,
  NormalizedContactImportPayload,
  normalizeContactImportPayload,
} from 'src/core/models/notification/contact-import-notification.payload';
import { ContactImportResultDialogComponent } from 'src/app/shared/components/contact-import-result-dialog/contact-import-result-dialog.component';
import { NotificationItemComponent } from '../../models/notification-item-component.model';

/**
 * Renders `type === 'ContactImportCompletedNotification'` — the outcome of an asynchronous CSV contact
 * import. The row click navigates to `/contacts` (see `notification-type-component.map.ts`), while the
 * "View" action opens the detailed result dialog in place instead of navigating.
 */
@Component({
  selector: 'app-contact-import-notification',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LocalizationModule, ContactImportResultDialogComponent],
  templateUrl: './contact-import-notification.component.html',
  styleUrl: './contact-import-notification.component.scss',
})
export class ContactImportNotificationComponent implements NotificationItemComponent {
  private _notification!: NotificationDto;

  payload: NormalizedContactImportPayload = normalizeContactImportPayload(null);

  messageParams: string[] = [];

  resultDialogVisible = false;

  @Input({ required: true })
  set notification(value: NotificationDto) {
    this._notification = value;
    this.payload = normalizeContactImportPayload(
      safeJsonParse<ContactImportNotificationPayload>(value.payload),
    );
    this.messageParams = [
      this.payload.fileName,
      String(this.payload.successCount),
      String(this.payload.totalRows),
    ];
  }

  get notification(): NotificationDto {
    return this._notification;
  }

  /**
   * "View" is offered for every outcome — success, partial failure and a wholly failed import all store a
   * result, so the user can always open the detail from the notifications page. Only a payload without an
   * import id (or one whose result could not be stored at all) has nothing to show.
   */
  get canViewResult(): boolean {
    return !!this.payload.importId && this.payload.hasStoredResult;
  }

  /**
   * The row itself is clickable (it marks the notification as read and navigates to `/contacts`), so the
   * click must not bubble — otherwise opening the result would also trigger the row's mark-as-read and
   * navigation behavior underneath the dialog.
   */
  openResult(event: Event): void {
    event.stopPropagation();
    this.resultDialogVisible = true;
  }
}
